import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css, keyframes } from 'styled-components';

import Icon from 'components/base/Icon';
import { H3, H4 } from 'components/base/Text';

import Account from 'layouts/shared/Account';
import Progress from './Progress';
import ProgressIcon from './ProgressIcon';

import Queries from 'graphql/Queries';

const enterContainerLeft = keyframes`
  0% {
    transform: translateX(-1.5rem);
  }
`;
const enterContainerDown = keyframes`
  0% {
    transform: translateY(-1.5rem);
  }
`;

const Container = styled.div`
  align-items: center;
  animation: ${enterContainerDown} 600ms var(--quintOut);
  background: var(--purpleTT);
  border-bottom: 1px solid var(--purple-gray-T);
  display: flex;
  justify-content: space-between;
  padding: 1.25rem 1rem;
  transition: 400ms width var(--quintOut);
  width: 100%;
  @media (min-width: 50em) {
    align-items: flex-start;
    animation: ${enterContainerLeft} 1000ms var(--quintOut);
    border-bottom: initial;
    flex-wrap: wrap;
    justify-content: center;
    min-height: 100vh;
    padding: initial;
  }
  @media (min-width: 69em) {
    width: 8.5rem;
    ${props => props.isOnboarding && css`
      width: 18rem;
    `}
  }

  @supports not (display: grid) {
    width: 100%;
    @media (min-width: 50em) {
      width: 8.5rem;
      ${props => props.isOnboarding && css`
        width: 18.5rem;
      `}
    }
  }
`;

const VerticalCenter = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-left: -0.875rem;
  margin-right: auto;
  width: initial;
  ${props => props.isOnboarding && css`
    margin-left: initial;
    margin-right: initial;
  `}
  @media (min-width: 50em) {
    display: block;
    margin: initial;
    width: 100%;
  }

  @supports not (display: grid) {
    width: 8.5rem;
    ${props => props.isOnboarding && css`
      width: 18.5rem;
    `}
  }
`;

const Project = styled.div`
  align-items: center;
  border-bottom: 0;
  display: flex;
  margin: 0 auto;
  padding-bottom: 0;

  h3 {
    color: var(--purpleS);
    font-size: 1em;
    margin: 6px 0 0;
    text-align: left;
  }
  h4 {
    display: none;
  }

  @media (min-width: 50em) {
    border-bottom: 2px solid var(--gray);
    flex-direction: column;
    margin: 0.75rem 0.75rem 0;
    padding-bottom: 2rem;
    text-align: center;
    ${props => props.isOnboarding && css`
      margin: 1.5rem 2.5rem 0;
      padding-bottom: 2.5rem;
    `}

    h3 {
      font-size: var(--step-down-1);
      margin-bottom: 0;
      text-align: center;
    }
    h4 {
      color: var(--purple-gray);
      display: block;
      font-size: var(--step-down-1);
      font-weight: 400;
      letter-spacing: 0;
      margin: 0;
      text-transform: inherit;
    }
  }
`;

const ProgressWrapper = styled.div`
  align-items: start;
  background: var(--purpleTT);
  border: 0;
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 1.5rem;
  position: fixed;
  right: 0;
  top: 0;
  transform: translateX(100%);
  transition: 400ms transform var(--quintOut);
  width: 80%;
  z-index: 3;
  ${props => props.show && css`
    transform: translateX(0);
  `}

  @media (min-width: 23em) {
    width: 19rem;
  }
  @media (min-width: 50em) {
    align-items: initial;
    background: initial;
    border: initial;
    height: initial;
    padding: initial;
    position: initial;
    right: initial;
    top: initial;
    transform: initial;
    width: initial;
    z-index: initial;
  }
  @media (max-height: 32em) {
    padding-top: 2.25rem;
  }
`;

const ProgressTitle = styled.h3`
  border-bottom: 2px solid rgba(126,124,151, 0.5);
  color: var(--purple-gray);
  font-size: var(--step-down-1);
  letter-spacing: 0.75px;
  margin: 0 auto 2rem;
  padding-bottom: 1.25rem;
  text-align: center;
  text-transform: uppercase;
  width: 100%;
  @media (min-width: 50em) {
    border-bottom: initial;
    margin: 2.5rem 0 2.25rem;
    padding-bottom: initial;
    width: initial;
  }
`;

const ProjectIcon = styled(({ isOnboarding, ...rest }) => <Link {...rest} />)`
  background: var(--${props => props.color});
  border-radius: 6.25rem;
  box-shadow: 0 0 0 0.375rem rgba(var(--${props => props.color}-rgb),0.3);
  color: rgba(var(--${props => props.color}-rgb), 0.3); // safari hack
  display: inline-flex;
  justify-content: center;
  justify-self: center;
  margin: 0 1rem 0 0.25rem;
  padding: 0.5em;

  svg {
    color: var(--white);
    height: 0.875rem;
    width: 0.875rem;
  }

  @media (min-width: 50em) {
    box-shadow: 0 0 0 0.4375rem rgba(var(--${props => props.color}-rgb), 0.3);
    margin: 2.5rem 0 0.5rem;
    padding: 0.625rem;
    ${props => props.isOnboarding && css`
      box-shadow: 0 0 0 0.625rem rgba(var(--${props => props.color}-rgb), 0.3); // safari hack
      margin: 1.5rem 0 0.75rem;
      padding: 1em;
    `}

    svg {
      height: 1.25rem;
      width: 1.25rem;
      ${props => props.isOnboarding && css`
        height: 1.65rem;
        width: 1.65rem;
      `}
    }
  }
`;

const Hamburger = styled.div`
  color: var(--purpleS);
  cursor: pointer;
  display: block;
  margin-top: 0.0625rem;
  margin-right: auto;
  ${props => props.isOnboarding && css`
    margin-right: initial;
  `}

  span {
    background-color: rgba(var(--purple-gray-rgb), 0.5);
    display: block;
    height: 0.1875rem;
    margin-bottom: 0.25rem;
    margin-right: 0.25rem;
    width: 1.5rem;
    &:last-of-type {
      margin-bottom: 0;
    }
  }

  @media (min-width: 50em) {
    display: none;
  }
`;

const Overlay = styled.div`
  ${props => props.active && css`
    background-color: rgba(102, 102, 102, 0.45);
    content: '';
    display: block;
    height: 100vh;
    left: 0;
    position: fixed;
    top: 0;
    width: 100vw;
    z-index: 3;
  `}
  @media (min-width: 50em) {
    display: none;
  }
`;

class Userbar extends React.Component {
  state = {
    hideProgress: false,
  };

  constructor(props) {
    super(props);

    this._handleProgressClick = this._handleProgressClick.bind(this);
  }

  _handleProgressClick() {
    this.setState({ hideProgress: !this.state.hideProgress });
  }

  render() {
    const { projectId, _handleMenuClick, setSidebarWidth } = this.props;

    return (
      <Queries.Project variables={{ id: projectId }}>
        {(project) => {
          setTimeout(() => { setSidebarWidth(project.isOnboarding) });
          return (
            <React.Fragment>
              <Overlay active={this.state.hideProgress} onClick={this._handleProgressClick} />
              <Container isOnboarding={project.isOnboarding} location='userbar'>
                <Hamburger onClick={_handleMenuClick} isOnboarding={project.isOnboarding}>
                  <span />
                  <span />
                  <span />
                </Hamburger>
                <VerticalCenter isOnboarding={project.isOnboarding}>
                  <Project isOnboarding={project.isOnboarding}>
                    <ProjectIcon to={`/project/${projectId}`} color={project.color} isOnboarding={project.isOnboarding}>
                      <Icon id="activity" title="Activity" />
                    </ProjectIcon>
                    <div>
                      <H3>{project.name}</H3>
                      <H4>{project.clientName}</H4>
                    </div>
                  </Project>
                  {project.isOnboarding &&
                    <ProgressWrapper show={this.state.hideProgress}>
                      <ProgressTitle>Project Progress</ProgressTitle>
                      <Progress projectId={projectId} />
                    </ProgressWrapper>
                  }
                </VerticalCenter>
                <Account isOnboarding={project.isOnboarding} location='userbar' />
                {project.isOnboarding &&
                  <ProgressIcon stepNumber={project.onboardingStage-1} _handleProgressClick={this._handleProgressClick} />
                }
              </Container>
            </React.Fragment>
          );
        }}
      </Queries.Project>
    );
  }
}

Userbar.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default Userbar;



// WEBPACK FOOTER //
// ./src/layouts/ProjectLayout/Userbar.js